import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { useGroup } from '../lib/GroupContext'
import Logo from '../components/Logo'
import Page from '../components/Page'
import TabBar from '../components/TabBar'

export default function Home() {
  const { user, profile, signOut } = useAuth()
  const { groups, activeGroup, loadingGroups, switchGroup, refreshGroups } = useGroup()
  const navigate = useNavigate()
  const [members, setMembers] = useState([])
  const [session, setSession] = useState(null)
  const [submissions, setSubmissions] = useState([])
  const [watchlistCount, setWatchlistCount] = useState(0)
  const [watchlistPosters, setWatchlistPosters] = useState([])
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [inviteInput, setInviteInput] = useState('')
  const [showGroupSwitcher, setShowGroupSwitcher] = useState(false)
  const [joinInput, setJoinInput] = useState('')
  const [showJoinForm, setShowJoinForm] = useState(false)

  useEffect(() => {
    loadData()

    // Real-time subscription for submissions + sessions
    const channel = supabase
      .channel('home-sessions')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'session_submissions' }, () => loadData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'movie_night_sessions' }, () => loadData())
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [user, activeGroup])

  async function loadData() {
    if (!user || !activeGroup) {
      setLoading(false)
      return
    }

    // All group members for progress count
    const { data: allMembers } = await supabase
      .from('group_members')
      .select('user_id, profiles!group_members_user_id_fkey(display_name)')
      .eq('group_id', activeGroup.id)
    setMembers(allMembers || [])

    // Active session (if any)
    const { data: activeSession } = await supabase
      .from('movie_night_sessions')
      .select('*')
      .eq('group_id', activeGroup.id)
      .in('status', ['open', 'spinning'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    setSession(activeSession || null)

    if (activeSession) {
      const { data: subs } = await supabase
        .from('session_submissions')
        .select('*, movies(*), profiles!session_submissions_user_id_fkey(display_name)')
        .eq('session_id', activeSession.id)
        .order('created_at', { ascending: true })
      setSubmissions(subs || [])
    } else {
      setSubmissions([])
    }

    // Personal watchlist count + poster previews
    const { data: watchlistItems, count } = await supabase
      .from('watchlist_items')
      .select('movie_id, movies(poster_url, title)', { count: 'exact' })
      .eq('user_id', user.id)
      .order('added_at', { ascending: false })
      .limit(6)
    setWatchlistCount(count || 0)
    setWatchlistPosters((watchlistItems || []).map(w => w.movies).filter(m => m?.poster_url))

    setLoading(false)
  }

  async function startMovieNight() {
    if (!activeGroup || starting) return
    setStarting(true)
    try {
      const { data, error } = await supabase
        .from('movie_night_sessions')
        .insert({
          group_id: activeGroup.id,
          created_by: user.id,
          status: 'open',
        })
        .select()
        .single()
      if (error) throw error
      setSession(data)
      await loadData()
    } catch (err) {
      console.error(err)
      alert('Could not start movie night. Try again.')
    } finally {
      setStarting(false)
    }
  }

  if (loading || loadingGroups) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <span className="text-[var(--fp-text-muted)]">Loading...</span>
      </div>
    )
  }

  if (groups.length === 0) {
    function handleJoinByLink(e) {
      e.preventDefault()
      const raw = inviteInput.trim()
      if (!raw) return
      const match = raw.match(/\/join\/([^/?#]+)/i)
      const code = match ? match[1] : raw
      navigate(`/join/${code}`)
    }

    return (
      <Page mode="cinema">
        <div className="w-full max-w-sm text-center">
          <div className="text-5xl mb-4">🎬</div>
          <h2 className="text-xl font-bold text-white mb-2">No group yet</h2>
          <p className="text-sm text-[var(--fp-text-muted)] mb-6">
            Paste an invite link from the crew, or start your own group.
          </p>

          <form onSubmit={handleJoinByLink} className="flex flex-col gap-2 mb-5">
            <input
              type="text"
              placeholder="Paste invite link or code"
              value={inviteInput}
              onChange={e => setInviteInput(e.target.value)}
              className="fp-input text-center"
            />
            <button type="submit" className="fp-btn fp-btn-primary" disabled={!inviteInput.trim()}>
              Join with invite
            </button>
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-[var(--fp-border)]" />
            <span className="text-[var(--fp-text-dim)] text-xs">or</span>
            <div className="flex-1 h-px bg-[var(--fp-border)]" />
          </div>

          <button onClick={() => navigate('/create-group')} className="fp-btn fp-btn-secondary">
            Create a new group
          </button>

          <button
            onClick={async () => { await signOut(); navigate('/login') }}
            className="text-xs text-[var(--fp-text-dim)] bg-transparent border-none cursor-pointer hover:text-[var(--fp-red)] mt-6"
          >
            Log out{user?.email ? ` (${user.email})` : ''}
          </button>
        </div>
      </Page>
    )
  }

  const mySubmission = submissions.find(s => s.user_id === user.id)
  const submittedCount = submissions.length
  const totalMembers = members.length || 1
  const everyoneIn = submittedCount >= totalMembers
  const readyToSpin = submittedCount >= 1 // can start spinning once at least one is in; but we show spin when there are 2+

  return (
    <Page mode="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between py-4 mb-14">
        <Logo size="md" />
        <div className="flex gap-2">
          <button onClick={() => navigate('/group')} className="w-12 h-12 rounded-full bg-[var(--fp-surface)] border border-[var(--fp-border)] flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--fp-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
          </button>
        </div>
      </div>

      {/* Group banner with switcher */}
      <div className="fp-card-elevated mb-16 relative">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowGroupSwitcher(!showGroupSwitcher)}
            className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold bg-transparent border-none cursor-pointer p-0 flex items-center gap-2 min-w-0 flex-1"
          >
            <span className="truncate">{activeGroup.name}</span>
            <svg className="flex-shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--fp-gold)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: showGroupSwitcher ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          <button
            onClick={() => navigate('/create-group')}
            className="text-xs font-semibold border-none cursor-pointer rounded-full hover:brightness-110 transition-all flex-shrink-0 whitespace-nowrap"
            style={{
              background: 'var(--fp-gold-soft)',
              color: 'var(--fp-gold)',
              padding: '8px 16px',
            }}
          >
            + New group
          </button>
        </div>

        {/* Group switcher dropdown */}
        {showGroupSwitcher && (
          <div className="mt-4 pt-4 border-t border-[var(--fp-border)]">
            <button
              onClick={() => { setShowGroupSwitcher(false); navigate('/solo') }}
              className="w-full flex items-center justify-between py-3 px-2 rounded-xl bg-transparent border-none cursor-pointer text-left hover:bg-[var(--fp-elevated)] transition-colors mb-1"
            >
              <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--fp-gold)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Solo Spin
              </span>
              <span className="text-xs text-[var(--fp-text-dim)]">Just you</span>
            </button>
            <div className="h-px bg-[var(--fp-border)] my-2" />
            {groups.map(g => (
              <button
                key={g.id}
                onClick={() => { switchGroup(g.id); setShowGroupSwitcher(false) }}
                className="w-full flex items-center justify-between py-3 px-2 rounded-xl bg-transparent border-none cursor-pointer text-left hover:bg-[var(--fp-elevated)] transition-colors"
              >
                <span className={`text-sm font-semibold ${g.id === activeGroup.id ? 'text-white' : 'text-[var(--fp-text-muted)]'}`}>
                  {g.name}
                </span>
                {g.id === activeGroup.id && (
                  <span className="text-xs" style={{ color: 'var(--fp-red)' }}>Active</span>
                )}
              </button>
            ))}

            {/* Join another group */}
            <div className="mt-3 pt-3 border-t border-[var(--fp-border)]">
              {!showJoinForm ? (
                <button
                  onClick={() => setShowJoinForm(true)}
                  className="w-full flex items-center gap-2 py-3 px-2 rounded-xl bg-transparent border-none cursor-pointer text-left hover:bg-[var(--fp-elevated)] transition-colors"
                >
                  <span className="text-sm" style={{ color: 'var(--fp-text-muted)' }}>🔗 Join another group</span>
                </button>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    const raw = joinInput.trim()
                    if (!raw) return
                    const match = raw.match(/\/join\/([^/?#]+)/i)
                    const code = match ? match[1] : raw
                    setShowJoinForm(false)
                    setShowGroupSwitcher(false)
                    setJoinInput('')
                    navigate(`/join/${code}`)
                  }}
                  className="flex flex-col gap-3 py-2"
                >
                  <p className="text-xs text-[var(--fp-text-dim)] m-0">Paste an invite link or code from the crew:</p>
                  <input
                    type="text"
                    placeholder="e.g. flickpick.com.au/join/abc123"
                    value={joinInput}
                    onChange={e => setJoinInput(e.target.value)}
                    className="fp-input text-sm w-full"
                    style={{ padding: '12px 14px', width: '100%', boxSizing: 'border-box' }}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setShowJoinForm(false); setJoinInput('') }}
                      className="text-xs text-[var(--fp-text-dim)] bg-transparent border-none cursor-pointer hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!joinInput.trim()}
                      className="text-xs font-semibold border-none cursor-pointer rounded-full hover:brightness-110 transition-all"
                      style={{
                        background: joinInput.trim() ? 'var(--fp-red)' : 'var(--fp-surface)',
                        color: joinInput.trim() ? '#fff' : 'var(--fp-text-dim)',
                        padding: '8px 20px',
                      }}
                    >
                      Join group
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3 mt-5">
          <span className="w-2.5 h-2.5 rounded-full bg-[var(--fp-red)] animate-pulse" />
          <span className="text-base text-[var(--fp-text-muted)]">
            {session
              ? `${submittedCount} of ${totalMembers} picked`
              : watchlistCount > 0
                ? `${watchlistCount} in your watchlist`
                : 'No movie night running'}
          </span>
        </div>
      </div>

      {/* Hero section — always visible */}
      <div className="flex flex-col items-center mb-14">
        {/* Poster fan — shows your watchlist movies as a teaser */}
        {watchlistPosters.length > 0 && (
          <div className="flex justify-center items-end mb-16" style={{ height: '200px', perspective: '800px' }}>
            {watchlistPosters.slice(0, 5).map((m, i) => {
              const total = Math.min(watchlistPosters.length, 5)
              const mid = (total - 1) / 2
              const offset = i - mid
              return (
                <div
                  key={i}
                  className="rounded-xl overflow-hidden shadow-2xl flex-shrink-0"
                  style={{
                    width: '100px',
                    height: '150px',
                    marginLeft: i === 0 ? 0 : '-20px',
                    transform: `rotate(${offset * 6}deg) translateY(${Math.abs(offset) * 8}px)`,
                    zIndex: total - Math.abs(offset),
                    border: '2px solid var(--fp-border)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
                  }}
                >
                  <img src={m.poster_url} alt={m.title} className="w-full h-full object-cover" />
                </div>
              )
            })}
          </div>
        )}

        {watchlistPosters.length === 0 && (
          <div className="text-8xl mb-16 opacity-20">🎬</div>
        )}

        <h2 className="fp-display text-5xl text-white mb-8 text-center" style={{ lineHeight: '1.05' }}>
          Ready for<br />movie night?
        </h2>

        <p className="text-lg text-[var(--fp-text-muted)] mb-16 max-w-md text-center" style={{ lineHeight: '1.7' }}>
          {watchlistCount > 0
            ? <>Your crew's got <span className="text-white font-semibold">{watchlistCount} movie{watchlistCount !== 1 ? 's' : ''}</span> on the table. {session ? 'Lock in your picks and let the wheel decide.' : 'Start a session, lock in your picks, and let the wheel decide.'}</>
            : <>Curate your watchlist. Rally the crew. Let the double spin decide what you're watching tonight.</>
          }
        </p>

        {!session && (
          <>
            <button
              onClick={startMovieNight}
              disabled={starting}
              className="fp-btn fp-btn-gold max-w-sm mx-auto"
              style={{
                padding: '24px 48px',
                fontSize: '20px',
                borderRadius: '18px',
                boxShadow: '0 16px 48px rgba(212, 175, 55, 0.35)',
                letterSpacing: '0.02em',
              }}
            >
              {starting ? 'Starting…' : 'Start Movie Night'}
            </button>

            {watchlistCount === 0 && (
              <button
                onClick={() => navigate('/add')}
                className="block mx-auto mt-10 text-base text-[var(--fp-red)] bg-transparent border-none cursor-pointer font-semibold"
              >
                Build your watchlist first →
              </button>
            )}
          </>
        )}
      </div>

      {/* Session live */}
      {session && (
        <>
          {/* Your pick card */}
          <div className="fp-card mb-14">
            <div className="text-[11px] fp-gold-text uppercase tracking-[0.18em] font-semibold">
              Your pick tonight
            </div>
            {mySubmission ? (
              <div className="flex gap-5 mt-5">
                <button
                  onClick={() => navigate(`/movie/${mySubmission.movies?.tmdb_id}`)}
                  className="w-20 h-[120px] rounded-xl overflow-hidden flex-shrink-0 bg-[var(--fp-border)] shadow-lg cursor-pointer border-none p-0"
                >
                  {mySubmission.movies?.poster_url ? (
                    <img src={mySubmission.movies.poster_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl">🎬</div>
                  )}
                </button>
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <button
                    onClick={() => navigate(`/movie/${mySubmission.movies?.tmdb_id}`)}
                    className="fp-display text-lg text-white truncate bg-transparent border-none cursor-pointer p-0 text-left hover:text-[var(--fp-red)] transition-colors"
                  >
                    {mySubmission.movies?.title}
                  </button>
                  <div className="text-xs text-[var(--fp-text-muted)] mt-1.5">
                    {[mySubmission.movies?.year, mySubmission.movies?.genre].filter(Boolean).join(' · ')}
                  </div>
                  <div className="flex gap-4 mt-3">
                    <button
                      onClick={() => navigate(`/movie/${mySubmission.movies?.tmdb_id}`)}
                      className="text-xs text-white bg-transparent border-none cursor-pointer p-0 font-semibold hover:text-[var(--fp-red)] transition-colors"
                    >
                      View details →
                    </button>
                    <button
                      onClick={() => navigate('/watchlist')}
                      className="text-xs text-[var(--fp-text-dim)] bg-transparent border-none cursor-pointer p-0 font-semibold hover:text-[var(--fp-red)] transition-colors"
                    >
                      Swap pick
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-5">
                <p className="text-sm text-[var(--fp-text-muted)] mb-4">
                  You haven't picked yet. Choose one from your watchlist.
                </p>
                <button
                  onClick={() => navigate('/watchlist')}
                  className="fp-btn fp-btn-primary"
                >
                  Pick from my watchlist
                </button>
              </div>
            )}
          </div>

          {/* Crew progress */}
          <div className="mb-16">
            <h3 className="text-[11px] fp-gold-text uppercase tracking-[0.2em] font-semibold mb-5 pl-2">
              The crew
            </h3>
            <div className="fp-card">
              {members.map(m => {
                const sub = submissions.find(s => s.user_id === m.user_id)
                const name = m.profiles?.display_name || 'Crew member'
                return (
                  <div key={m.user_id} className="flex items-center justify-between py-4 border-b border-[var(--fp-border)] last:border-b-0 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <span className="w-[22px] h-[22px] rounded-full bg-[var(--fp-border-accent)] text-white text-[10px] flex items-center justify-center font-semibold">
                        {name.charAt(0).toUpperCase()}
                      </span>
                      <span className="text-sm text-white">{name}{m.user_id === user.id ? ' (you)' : ''}</span>
                    </div>
                    <span className="text-xs" style={{ color: sub ? 'var(--fp-red)' : 'var(--fp-text-dim)' }}>
                      {sub ? '✓ in' : 'waiting'}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Spin button — the hero moment */}
          {submittedCount >= 2 && (
            <div className="py-8">
              <button
                onClick={() => navigate('/wheel')}
                className="w-full rounded-3xl border-none text-white cursor-pointer fp-display"
                style={{
                  paddingTop: '36px',
                  paddingBottom: '36px',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  background: 'linear-gradient(135deg, var(--fp-red), var(--fp-red-hover))',
                  boxShadow: '0 20px 60px var(--fp-red-glow), 0 0 0 1px rgba(255,255,255,0.05) inset',
                  letterSpacing: '-0.02em',
                }}
              >
                <span className="block text-[11px] fp-gold-text uppercase tracking-[0.3em] font-semibold mb-3" style={{ fontFamily: 'var(--fp-font-body)' }}>
                  {everyoneIn ? "Everyone's in" : `${submittedCount} picks ready`}
                </span>
                <span className="block text-4xl font-extrabold">
                  Spin the Wheel
                </span>
                <span className="block text-sm font-normal opacity-80 mt-3" style={{ fontFamily: 'var(--fp-font-body)' }}>
                  Tap to spin →
                </span>
              </button>
            </div>
          )}

          {submittedCount < 2 && (
            <div className="py-12 text-center">
              <p className="text-sm text-[var(--fp-text-dim)]">
                Need at least 2 picks before the wheel can spin.
              </p>
            </div>
          )}
        </>
      )}

      <div className="text-center mt-20 mb-4">
        <button
          onClick={async () => { await signOut(); navigate('/login') }}
          className="text-xs text-[var(--fp-text-dim)] bg-transparent border-none cursor-pointer hover:text-[var(--fp-red)]"
        >
          Log out{user?.email ? ` (${user.email})` : ''}
        </button>
      </div>

      <TabBar />
    </Page>
  )
}
